import { Lock, Pencil } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { customValueProblem, customValueText, type CustomFieldModule, type CustomFieldValue, type CustomFieldValueView, type SaveCustomValuesInput } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { ModuleGate } from '../../../components/ModuleGate';
import { SkeletonLines } from '../../../components/Skeleton';
import { errorMessage } from '../../../lib/api-client';
import { EMPTY, formatIsoDate, formatNumber } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { useCustomValues, useSaveCustomValues } from '../api/settings-api';
import { CustomFieldControl, wireValue } from './CustomFieldControl';
import styles from './CustomFieldsCard.module.css';

interface CustomFieldsCardProps {
  readonly module: CustomFieldModule;
  readonly entityId: string;
  readonly canEdit: boolean;
}

/**
 * ADMIN-13 on a record (DECISIONS-LOG S29–S32 Q19): the organisation's own fields, read as a
 * list and edited in place. A field the viewer may not see reads `—` with a lock (#5) — never
 * silently missing. With no fields set up, the card is not drawn at all.
 */
export function CustomFieldsCard(props: CustomFieldsCardProps): ReactNode {
  return (
    <ModuleGate module="custom_fields">
      <CustomFieldsList {...props} />
    </ModuleGate>
  );
}

function CustomFieldsList({ module, entityId, canEdit }: CustomFieldsCardProps): ReactNode {
  const values = useCustomValues(module, entityId);
  // Held here, not in the editor: the editor closes the moment Save is pressed (§5a rule 1),
  // and a mutation owned by an unmounted component drops its success and failure toasts.
  const save = useSaveCustomValues(module);
  const show = useToasts((state) => state.show);
  const [isEditing, setIsEditing] = useState(false);
  if (values.isPending) return <SkeletonLines count={3} />;
  if (values.error || values.data.length === 0) return null;
  const isEditable = canEdit && values.data.some((row) => !row.isRestricted);

  return (
    <Card
      title="More details"
      action={
        isEditable && !isEditing ? (
          <Button size="sm" variant="ghost" icon={Pencil} onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        ) : undefined
      }
    >
      {isEditing ? (
        <Editor
          rows={values.data}
          onCancel={() => setIsEditing(false)}
          onSave={(input) => {
            setIsEditing(false);
            save.mutate(
              { entityId, input },
              {
                onSuccess: () => show({ tone: 'success', message: 'Details saved' }),
                onError: (cause) => show({ tone: 'danger', message: "The details weren't saved", detail: errorMessage(cause) }),
              },
            );
          }}
        />
      ) : (
        <dl className={styles.list}>
          {values.data.map((row) => (
            <div key={row.field.id} className={styles.pair}>
              <dt className={styles.label}>{row.field.label}</dt>
              <dd className={styles.value}>
                {row.isRestricted ? (
                  <span className={styles.restricted}>
                    {EMPTY}
                    <Lock size={12} aria-label="You can't see this field" />
                  </span>
                ) : (
                  readable(row)
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </Card>
  );
}

function readable(row: CustomFieldValueView): string {
  if (row.value === null) return EMPTY;
  if (row.field.fieldType === 'date' && typeof row.value === 'string') return formatIsoDate(row.value);
  if (typeof row.value === 'number') return formatNumber(row.value);
  return customValueText(row.value) || EMPTY;
}

function Editor({ rows, onCancel, onSave }: { readonly rows: readonly CustomFieldValueView[]; readonly onCancel: () => void; readonly onSave: (input: SaveCustomValuesInput) => void }): ReactNode {
  const editable = rows.filter((row) => !row.isRestricted);
  const [draft, setDraft] = useState<Readonly<Record<string, CustomFieldValue | null>>>(() => Object.fromEntries(editable.map((row) => [row.field.id, row.value])));
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});

  function submit(): void {
    const problems = Object.fromEntries(editable.flatMap((row) => {
      const problem = customValueProblem(row.field, draft[row.field.id] ?? null);
      return problem ? [[row.field.id, problem]] : [];
    }));
    setErrors(problems);
    if (Object.keys(problems).length > 0) return;
    onSave({ values: Object.fromEntries(editable.map((row) => [row.field.id, wireValue(draft[row.field.id] ?? null)])) });
  }

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {editable.map((row) => (
        <CustomFieldControl key={row.field.id} field={row.field} value={draft[row.field.id] ?? null} error={errors[row.field.id]} onChange={(value) => setDraft({ ...draft, [row.field.id]: value })} />
      ))}
      <div className={styles.actions}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" type="submit">
          Save
        </Button>
      </div>
    </form>
  );
}
